import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AimsLogo } from '@aimsos/ui';
import {
  useAuth,
  decodeJwtPayload,
  getCurrentSlugFromHostname,
  redirectToWorkspaceSubdomainWithHandoff,
  consumePendingWorkspaceRedirect,
} from '@aimsos/auth';
import { useWorkspaces } from '../hooks/useWorkspaces.js';

/**
 * Decides where an authenticated browser should land, Jira/Slack-style —
 * this is the piece that replaces the old "redirect straight to the JWT's
 * workspace_slug, no matter what" behavior with a real choice when there is
 * one to make:
 *
 * - Already on the {slug}.{rootDomain} matching the active JWT's
 *   workspace_slug (or subdomain routing isn't configured for this
 *   environment) — render children immediately, nothing to decide.
 * - On a DIFFERENT subdomain than the one the token resolved to, but that
 *   subdomain's slug matches one of the user's OWN workspaces — honor the
 *   URL instead of the token's default. This is what makes "go straight to
 *   workspace-1.{rootDomain} and log in from there" work like Slack/Jira:
 *   Auth0 only ever resolves identity, so the token exchange always falls
 *   back to the user's default workspace, not necessarily the one implied
 *   by the URL the person actually typed. Silently switches (see
 *   useWorkspaces.js's switchTo) instead of asking them to pick again.
 * - On the wrong subdomain (or the bare root domain), no URL match, and the
 *   user has access to exactly one workspace — no real choice to make, hand
 *   off there directly (see tokenHandoff.js) instead of making them click
 *   through a picker with a single option.
 * - On the wrong subdomain with no URL match and MORE than one workspace
 *   available — show the picker (`/select-workspace`) instead of guessing
 *   which one the user meant.
 *
 * Mounted once for the whole authenticated app lifetime (it wraps
 * ShellLayout at the `/*` route in App.jsx, which React Router keeps as a
 * single instance across in-app navigation), so this only re-evaluates when
 * the backend JWT itself changes — not on every route change.
 */
const WorkspaceGate = ({ children }) => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { tenants, isLoading, switchTo } = useWorkspaces();
  const [decided, setDecided] = useState(false);
  const hasActedRef = useRef(false);

  useEffect(() => {
    if (isLoading || hasActedRef.current) return;

    const profile = user ? { name: user.name, email: user.email, picture: user.picture } : undefined;

    // Someone typed {slug}.{rootDomain} directly while signed out and got
    // bounced through the base URL to actually run Auth0 there (see
    // LoginPage.jsx — Auth0 is only ever invoked from that one fixed
    // origin). Honor the workspace they originally asked for instead of
    // whatever the token's own default workspace happens to be, same as
    // the "URL names a workspace the user has access to" case below —
    // this is just that same intent carried across the login detour.
    const pending = consumePendingWorkspaceRedirect();
    if (pending) {
      const matchByPending = tenants.find((t) => t.slug === pending.slug);
      if (matchByPending) {
        hasActedRef.current = true;
        // switchTo() no-ops when the target is already the "current"
        // workspace — correct for in-app UI clicks, but wrong here: we're
        // sitting on the base origin (from the login detour), not on the
        // target subdomain yet, even when it happens to be the token's own
        // default workspace. Hand off directly with the EXISTING token in
        // that case instead of going through switchTo.
        if (matchByPending.id === decodeJwtPayload(token)?.tenant_uuid) {
          redirectToWorkspaceSubdomainWithHandoff(
            pending.slug,
            { token, profile },
            { path: pending.path },
          );
        } else {
          switchTo(matchByPending.id, { path: pending.path });
        }
        return;
      }
      // Stale/invalid slug (typo, no longer has access) — fall through to
      // the normal decision below using the token's own default workspace.
    }

    const slug = decodeJwtPayload(token)?.workspace_slug;
    const currentSlug = getCurrentSlugFromHostname();

    // Already on the workspace's own subdomain (or the environment has no
    // root domain configured, so subdomain routing is a no-op) — nothing to
    // decide.
    if (!slug || currentSlug === slug) {
      /*
       * Not derivable while rendering: reaching this branch depends on
       * consumePendingWorkspaceRedirect() above, which CONSUMES the pending redirect — so
       * re-evaluating the same chain during render would drop the workspace the user originally
       * asked for on the login detour. The extra render this costs is one frame of this
       * component's own loading screen, which is the deliberate design here (see the navigate()
       * comment below for why the other branches never set it at all).
       */
      // eslint-disable-next-line react-hooks/set-state-in-effect -- see comment above
      setDecided(true);
      return;
    }

    hasActedRef.current = true;

    // The URL itself names a specific workspace (e.g. the person typed
    // workspace-1.{rootDomain} directly) and it's one the user actually has
    // access to — honor that instead of the token's default workspace.
    const matchByUrl = currentSlug
      ? tenants.find((t) => t.slug === currentSlug)
      : null;
    if (matchByUrl) {
      switchTo(matchByUrl.id);
      return;
    }

    if (tenants.length <= 1) {
      redirectToWorkspaceSubdomainWithHandoff(slug, { token, profile });
      return;
    }

    // Deliberately NOT calling setDecided(true) here: doing so used to render
    // `children` (ShellLayout) in the same tick as this navigate() call.
    // ShellLayout mounts <Outlet/>, whose `index` route immediately fires its
    // own `<Navigate to="/dashboard" replace />` — a second, competing
    // replace-navigation that wins the race and overwrites this one, so the
    // browser ends up back on /dashboard instead of /select-workspace. Stay
    // on this component's own loading screen (decided=false) — it unmounts
    // on its own once the route actually switches away from `/*`.
    navigate('/select-workspace', { replace: true });
  }, [isLoading, tenants, token, user, navigate, switchTo]);

  if (!decided) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-950 via-purple-950 to-teal-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-900/40 via-transparent to-transparent" />
        <div className="absolute left-1/4 top-1/4 h-96 w-96 rounded-full bg-purple-600/20 blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 h-80 w-80 rounded-full bg-teal-600/20 blur-3xl" />
        <div className="relative z-10 flex flex-col items-center gap-6">
          <AimsLogo size={64} className="drop-shadow-2xl" />
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            <span className="text-sm font-medium text-white/70">Loading your workspaces…</span>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default WorkspaceGate;
